import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import ChatInterface from '../../components/copilot/ChatInterface';
import {
    Building2,
    PlaneTakeoff,
    Plane,
    Workflow,
    ArrowLeftRight,
    Bot,
    FileText,
    Settings,
    User,
    Globe,
    Sun,
    Moon,
    LogOut,
    ChevronDown,
    TrendingUp,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/*  ──────────────────────────────────────────────────────────
    Aipron DACAMS — Hub Home Page (v3 Corporate Consulting)
    ────────────────────────────────────────────────────────── */

const TEXTS = {
    es: {
        eyebrow: 'Centro de Análisis',
        welcome: 'Bienvenido/a,',
        subtitle: 'Herramientas de análisis y planificación de capacidad aeroportuaria',
        quickAccess: 'Acceso Rápido',
        askCopilot: 'Preguntar a Aipron CoPilot',
        training: 'Entrenamiento AI-AMSO',
        reports: 'Informes',
        profile: 'Mi Perfil',
        settings: 'Preferencias',
        logout: 'Cerrar Sesión',
        // Module descriptions
        dacamsDesc: 'AP-CAM, ADOSA, Tri-node Equilibrium, SVS y KPIs. Metodología completa de evaluación de capacidad.',
        aircapDesc: 'Capacidad de Terminal de Pasajeros',
        runcapDesc: 'Análisis de Capacidad del Sistema de Pistas',
        ftsDesc: 'Simulación en Tiempo Rápido',
        optimizerDesc: 'SVS — Puntuación de Viabilidad de Slots y Asignación',
        dashboardDesc: 'Comparación de capacidad As-is vs To-be',
        trainingDesc: 'Entrenamiento de modelos AI-AMSO para análisis de capacidad',
        reportsDesc: 'Generación de informes y documentación del proyecto',
        staffRosterDesc: 'Planificación de personal SDP. Rosters anuales/mensuales, alertas de guardia y previsión de demanda.',
        economicsDesc: 'Análisis económico CBA/ROI. Ahorro de combustible, CO2, delay y Business Case con estándares Eurocontrol.',
    },
    en: {
        eyebrow: 'Analysis Hub',
        welcome: 'Welcome back,',
        subtitle: 'Airport capacity analysis & planning tools',
        quickAccess: 'Quick Access',
        askCopilot: 'Ask Aipron CoPilot',
        training: 'AI-AMSO Training',
        reports: 'Reports',
        profile: 'My Profile',
        settings: 'Preferences',
        logout: 'Sign Out',
        dacamsDesc: 'AP-CAM, ADOSA, Tri-node Equilibrium, SVS & KPIs. Full capacity assessment methodology.',
        aircapDesc: 'Passenger Terminal Building Capacity',
        runcapDesc: 'Runway System Capacity Analysis',
        ftsDesc: 'Fast Time Simulation',
        optimizerDesc: 'SVS — Slot Viability Score & Allocation',
        dashboardDesc: 'As-is vs To-be capacity comparison',
        trainingDesc: 'AI-AMSO model training for capacity analysis',
        reportsDesc: 'Project report generation and documentation',
        staffRosterDesc: 'SDP staff planning. Annual/monthly rosters, guard activation alerts and demand forecasting.',
        economicsDesc: 'CBA/ROI economic analysis. Fuel, CO2, delay savings and Business Case using Eurocontrol standards.',
    },
};

const HubHomePage: React.FC = () => {
    const { user, logout } = useAuth();
    const { language, toggleLanguage } = useLanguage();
    const { theme, toggleTheme } = useTheme();
    const tx = TEXTS[language as 'es' | 'en'] || TEXTS.en;
    const [isCopilotOpen, setIsCopilotOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    // Reusable node/module button — matches the north-star home mockup.
    const sectionLabel = (dot: string, text: string) => (
        <div className="flex items-center gap-2 mb-2">
            <span className="rounded-full" style={{ width: 6, height: 6, background: dot }} />
            <span className="text-[13px] font-semibold" style={{ color: dot === 'var(--aipron-accent)' ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{text}</span>
        </div>
    );

    const userInitials = user?.full_name
        ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        : 'U';

    return (
        <div className="min-h-screen" style={{ background: 'var(--bg)' }}>

            {/* ── Top Utility Bar ── */}
            <div className="max-w-6xl mx-auto px-6 pt-5 pb-0">
                <div className="flex items-center justify-end gap-2">

                    {/* Language Toggle Pill */}
                    <button
                        onClick={toggleLanguage}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold tracking-wide transition-all duration-200 cursor-pointer"
                        style={{
                            background: 'var(--surface)',
                            border: '1px solid var(--border)',
                            color: 'var(--text-secondary)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--aipron-accent)'; e.currentTarget.style.color = 'var(--aipron-accent)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                        title={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
                    >
                        <Globe size={14} />
                        <span>{language === 'es' ? 'ES' : 'EN'}</span>
                        <span style={{ opacity: 0.35 }}>|</span>
                        <span style={{ opacity: 0.5 }}>{language === 'es' ? 'EN' : 'ES'}</span>
                    </button>

                    {/* Theme Toggle */}
                    <button
                        onClick={toggleTheme}
                        className="p-2 rounded-lg transition-all duration-200 cursor-pointer"
                        style={{
                            background: 'var(--surface)',
                            border: '1px solid var(--border)',
                            color: 'var(--text-secondary)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--aipron-accent)'; e.currentTarget.style.color = 'var(--aipron-accent)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                        title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                    >
                        {isDark ? <Sun size={16} /> : <Moon size={16} />}
                    </button>

                    {/* Settings Button */}
                    <button
                        onClick={() => navigate('/settings')}
                        className="p-2 rounded-lg transition-all duration-200 cursor-pointer"
                        style={{
                            background: 'var(--surface)',
                            border: '1px solid var(--border)',
                            color: 'var(--text-secondary)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--aipron-accent)'; e.currentTarget.style.color = 'var(--aipron-accent)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                        title={tx.settings}
                    >
                        <Settings size={16} />
                    </button>

                    {/* Separator */}
                    <div style={{ width: '1px', height: '24px', background: 'var(--border)', margin: '0 4px' }} />

                    {/* Profile Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setIsProfileOpen(!isProfileOpen)}
                            className="flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-xl transition-all duration-200 cursor-pointer"
                            style={{
                                background: 'var(--surface)',
                                border: '1px solid var(--border)',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--aipron-accent)'; }}
                            onMouseLeave={(e) => { if (!isProfileOpen) e.currentTarget.style.borderColor = 'var(--border)'; }}
                        >
                            {/* Avatar */}
                            <div
                                className="flex items-center justify-center rounded-lg text-[11px] font-bold text-white"
                                style={{
                                    width: '30px',
                                    height: '30px',
                                    background: 'linear-gradient(135deg, #F26430, #D85528)',
                                    letterSpacing: '0.02em',
                                }}
                            >
                                {userInitials}
                            </div>
                            <div className="text-left hidden sm:block" style={{ minWidth: '80px' }}>
                                <div className="text-[12px] font-semibold leading-tight" style={{ color: 'var(--text-primary)' }}>
                                    {user?.full_name?.split(' ')[0] || 'User'}
                                </div>
                                <div className="text-[10px] leading-tight" style={{ color: 'var(--text-tertiary)' }}>
                                    {user?.role === 'superadmin' ? 'Admin' : user?.role === 'client_admin' ? 'Client Admin' : 'Viewer'}
                                </div>
                            </div>
                            <ChevronDown
                                size={14}
                                style={{
                                    color: 'var(--text-tertiary)',
                                    transition: 'transform 200ms',
                                    transform: isProfileOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            />
                        </button>

                        {/* Dropdown Menu */}
                        {isProfileOpen && (
                            <>
                                {/* Backdrop */}
                                <div
                                    className="fixed inset-0 z-40"
                                    onClick={() => setIsProfileOpen(false)}
                                />
                                {/* Menu */}
                                <div
                                    className="absolute right-0 top-full mt-2 z-50 rounded-xl overflow-hidden shadow-lg"
                                    style={{
                                        background: 'var(--surface)',
                                        border: '1px solid var(--border)',
                                        minWidth: '200px',
                                        animation: 'fadeInDown 150ms ease-out',
                                    }}
                                >
                                    {/* User info */}
                                    <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                        <div className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                                            {user?.full_name || 'User'}
                                        </div>
                                        <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                                            {user?.email || ''}
                                        </div>
                                    </div>

                                    {/* Menu Items */}
                                    <div className="py-1">
                                        <button
                                            onClick={() => { setIsProfileOpen(false); navigate('/profile'); }}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] transition-colors duration-150"
                                            style={{ color: 'var(--text-secondary)' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-raised)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                                        >
                                            <User size={15} />
                                            {tx.profile}
                                        </button>
                                        <button
                                            onClick={() => { setIsProfileOpen(false); navigate('/settings'); }}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] transition-colors duration-150"
                                            style={{ color: 'var(--text-secondary)' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-raised)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                                        >
                                            <Settings size={15} />
                                            {tx.settings}
                                        </button>
                                    </div>

                                    {/* Logout */}
                                    <div style={{ borderTop: '1px solid var(--border-subtle)' }} className="py-1">
                                        <button
                                            onClick={() => { setIsProfileOpen(false); logout(); navigate('/login'); }}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] transition-colors duration-150"
                                            style={{ color: 'var(--aipron-danger)' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-raised)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                        >
                                            <LogOut size={15} />
                                            {tx.logout}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 py-8">

                {/* Header */}
                <div className="mb-8 pb-6" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <span className="eyebrow">{tx.eyebrow}</span>
                    <h1 className="text-[2.25rem] mt-1"
                        style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.03em', lineHeight: 1.15 }}>
                        {tx.welcome} {user?.full_name?.split(' ')[0] || 'Analyst'}
                    </h1>
                    <p className="text-[15px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                        {tx.subtitle}
                    </p>
                </div>

                {/* ── Flujo principal — plan de capacidad ── */}
                {sectionLabel('var(--aipron-accent)', language === 'es' ? 'Flujo principal — plan de capacidad' : 'Primary flow — capacity plan')}
                <div className="rounded-xl mb-6 p-5" style={{ border: '2px solid var(--aipron-accent)', background: 'var(--surface)' }}>
                    <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                        {language === 'es' ? 'Nodos de entrada — el tri-nodo combina los tres subsistemas' : 'Input nodes — the tri-node combines the three subsystems'}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        <button onClick={() => navigate('/runcap')} className="text-left rounded-lg p-3 transition-colors hover:brightness-95" style={{ border: '1px solid var(--border)', background: 'var(--surface-raised)' }}>
                            <PlaneTakeoff className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                            <div className="text-[13px] font-bold mt-1" style={{ color: 'var(--text-primary)' }}>RunCap</div>
                            <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{language === 'es' ? 'Pista · RRC' : 'Runway · RRC'}</div>
                        </button>
                        <button onClick={() => navigate('/analysis')} className="text-left rounded-lg p-3 transition-colors hover:brightness-95" style={{ border: '1.5px solid var(--aipron-accent)', background: 'var(--accent-soft)' }}>
                            <Plane className="w-5 h-5" style={{ color: 'var(--aipron-accent)' }} />
                            <div className="text-[13px] font-bold mt-1" style={{ color: 'var(--aipron-accent)' }}>AP-CAM</div>
                            <div className="text-[11px]" style={{ color: 'var(--aipron-accent)', opacity: 0.8 }}>{language === 'es' ? 'Plataforma · RAC' : 'Apron · RAC'}</div>
                        </button>
                        <button onClick={() => navigate('/aircap')} className="text-left rounded-lg p-3 transition-colors hover:brightness-95" style={{ border: '1px solid var(--border)', background: 'var(--surface-raised)' }}>
                            <Building2 className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                            <div className="text-[13px] font-bold mt-1" style={{ color: 'var(--text-primary)' }}>AirCap</div>
                            <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>Terminal · PTBC</div>
                        </button>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => navigate('/projects/new')} className="px-4 py-2 rounded-lg text-[13px] font-bold text-white transition-all active:scale-95" style={{ background: 'var(--aipron-accent)' }}>
                            {language === 'es' ? 'Nuevo análisis' : 'New analysis'}
                        </button>
                        <button onClick={() => navigate('/analysis')} className="px-4 py-2 rounded-lg text-[13px] transition-colors hover:bg-[var(--surface-raised)]" style={{ border: '1px solid var(--border)', color: 'var(--text-primary)', background: 'transparent' }}>
                            {language === 'es' ? 'Ver proyectos' : 'View projects'}
                        </button>
                    </div>
                </div>

                {/* ── Caso de negocio — opcional ── */}
                {sectionLabel('var(--text-tertiary)', language === 'es' ? 'Caso de negocio — opcional' : 'Business case — optional')}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    {[
                        { icon: Workflow, title: 'FTS + AI-AMSO', sub: language === 'es' ? 'Escenarios to-be' : 'To-be scenarios', path: '/fts' },
                        { icon: ArrowLeftRight, title: language === 'es' ? 'Escenarios' : 'Scenarios', sub: 'As-is / to-be', path: '/dashboard' },
                        { icon: TrendingUp, title: 'Economics', sub: 'CBA · ROI', path: '/economics' },
                    ].map((m) => (
                        <button key={m.path} onClick={() => navigate(m.path)} className="text-left rounded-xl p-3 transition-colors hover:brightness-95" style={{ border: '1px solid var(--border)', background: 'var(--surface-raised)' }}>
                            <m.icon className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                            <div className="text-[13px] font-bold mt-1.5" style={{ color: 'var(--text-primary)' }}>{m.title}</div>
                            <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{m.sub}</div>
                        </button>
                    ))}
                </div>

                {/* ── Entregable y utilidades ── */}
                {sectionLabel('var(--text-tertiary)', language === 'es' ? 'Entregable y utilidades' : 'Deliverable and utilities')}
                <div className="flex flex-wrap gap-3 mb-4">
                    <button onClick={() => navigate('/reports')} className="flex-1 min-w-[150px] rounded-lg p-3 flex items-center gap-2.5 transition-colors hover:bg-[var(--surface-raised)]" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}>
                        <FileText className="w-5 h-5" style={{ color: 'var(--aipron-accent)' }} />
                        <div className="text-left"><div className="text-[13px] font-bold" style={{ color: 'var(--text-primary)' }}>{language === 'es' ? 'Informe APCAP' : 'APCAP report'}</div><div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{language === 'es' ? 'El entregable' : 'The deliverable'}</div></div>
                    </button>
                    <button onClick={() => setIsCopilotOpen(true)} className="flex-1 min-w-[150px] rounded-lg p-3 flex items-center gap-2.5 transition-colors hover:bg-[var(--surface-raised)]" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}>
                        <Bot className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                        <div className="text-left"><div className="text-[13px] font-bold" style={{ color: 'var(--text-primary)' }}>CoPilot</div><div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{language === 'es' ? 'Asistente' : 'Assistant'}</div></div>
                    </button>
                    <button onClick={() => navigate('/settings')} className="flex-1 min-w-[150px] rounded-lg p-3 flex items-center gap-2.5 transition-colors hover:bg-[var(--surface-raised)]" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}>
                        <Settings className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                        <div className="text-left"><div className="text-[13px] font-bold" style={{ color: 'var(--text-primary)' }}>{language === 'es' ? 'Ajustes' : 'Settings'}</div><div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{language === 'es' ? 'Preferencias' : 'Preferences'}</div></div>
                    </button>
                </div>


                {/* Footer */}
                <div className="mt-10 pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                        DACAMS v3.0 · Aipron Consulting Technologies
                    </p>
                </div>
            </div>

            {/* Floating CoPilot FAB */}
            <button
                onClick={() => setIsCopilotOpen(true)}
                className="copilot-fab"
                title={tx.askCopilot}
                style={{
                    position: 'fixed',
                    bottom: 28,
                    right: 28,
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #F26430, #D85528)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(242,100,48,0.35), 0 2px 4px rgba(0,0,0,0.15)',
                    zIndex: 40,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.08)';
                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(242,100,48,0.45), 0 3px 8px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(242,100,48,0.35), 0 2px 4px rgba(0,0,0,0.15)';
                }}
            >
                <Bot size={24} color="#fff" />
                {/* Pulse ring */}
                <span style={{
                    position: 'absolute',
                    inset: -4,
                    borderRadius: '50%',
                    border: '2px solid rgba(242,100,48,0.3)',
                    animation: 'fabPulse 2s ease-in-out infinite',
                }} />
            </button>

            {/* CoPilot Chat Interface */}
            <ChatInterface
                projectId=""
                isOpen={isCopilotOpen}
                onClose={() => setIsCopilotOpen(false)}
            />

            {/* Animations */}
            <style>{`
                @keyframes fadeInDown {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes fabPulse {
                    0%, 100% { transform: scale(1); opacity: 0.6; }
                    50% { transform: scale(1.15); opacity: 0; }
                }
            `}</style>
        </div>
    );
};

export default HubHomePage;

